import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback } from './ui/avatar';
import { toast } from 'sonner';
import { 
  Calendar as CalendarIcon, 
  Sparkles, 
  X, 
  UserPlus, 
  AlertCircle, 
  AlertTriangle, 
  CheckCircle,
  Activity
} from 'lucide-react';
import { format, addDays } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CreateTaskModal = ({ open, onClose, onTaskCreated }) => {
  const { token, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [userWorkloads, setUserWorkloads] = useState({});
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    due_date: addDays(new Date(), 7),
    assigned_to: '',
    secondary_participants: []
  });
  const [dateOpen, setDateOpen] = useState(false);

  useEffect(() => {
    const fetchUsersAndWorkloads = async () => {
      try {
        // Fetch users
        const usersResponse = await fetch(`${API}/users`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (usersResponse.ok) {
          const usersData = await usersResponse.json();
          setUsers(usersData);
        }

        // Fetch workloads for all users
        const workloadResponse = await fetch(`${API}/users/workload`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (workloadResponse.ok) {
          const workloadData = await workloadResponse.json();
          const workloadMap = {};
          workloadData.forEach(w => {
            workloadMap[w.user_id] = w;
          });
          setUserWorkloads(workloadMap);
        }
      } catch (error) {
        console.error('Error fetching users/workloads:', error);
      }
    };

    if (open) {
      fetchUsersAndWorkloads();
      // Reset form when opening
      setFormData({
        name: '',
        description: '',
        due_date: addDays(new Date(), 7),
        assigned_to: '',
        secondary_participants: []
      });
    }
  }, [open, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Task name is required');
      return;
    }
    if (!formData.assigned_to) {
      toast.error('Please select a primary assignee');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/tasks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description,
          due_date: format(formData.due_date, 'yyyy-MM-dd'),
          assigned_to: formData.assigned_to,
          secondary_participants: formData.secondary_participants
        })
      });

      if (response.ok) {
        setFormData({
          name: '',
          description: '',
          due_date: addDays(new Date(), 7),
          assigned_to: '',
          secondary_participants: []
        });
        onTaskCreated();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create task');
      }
    } catch (error) {
      toast.error('Failed to create task');
    } finally {
      setLoading(false);
    }
  };

  const addSecondaryParticipant = (userId) => {
    if (userId === formData.assigned_to) {
      toast.error('This user is already the primary assignee');
      return;
    }
    if (!formData.secondary_participants.includes(userId)) {
      setFormData({
        ...formData,
        secondary_participants: [...formData.secondary_participants, userId]
      });
    }
  };

  const removeSecondaryParticipant = (userId) => {
    setFormData({
      ...formData,
      secondary_participants: formData.secondary_participants.filter(id => id !== userId)
    });
  };

  const getWorkloadIndicator = (userId) => {
    const workload = userWorkloads[userId];
    if (!workload) return null;

    const total = workload.total_active;
    
    if (total >= 10 || workload.overdue_tasks >= 3) {
      return {
        icon: <AlertCircle className="w-4 h-4 text-red-500" />,
        badge: 'bg-red-500/20 text-red-600 dark:text-red-400',
        text: `${total} tasks (Overloaded!)`,
        level: 'critical'
      };
    } else if (total >= 7 || workload.overdue_tasks >= 2) {
      return {
        icon: <AlertTriangle className="w-4 h-4 text-orange-500" />,
        badge: 'bg-orange-500/20 text-orange-600 dark:text-orange-400',
        text: `${total} tasks (Heavy)`,
        level: 'high'
      };
    } else if (total >= 5 || workload.overdue_tasks >= 1) {
      return {
        icon: <Activity className="w-4 h-4 text-amber-500" />,
        badge: 'bg-amber-500/20 text-amber-600 dark:text-amber-400',
        text: `${total} tasks (Moderate)`,
        level: 'medium'
      };
    } else {
      return {
        icon: <CheckCircle className="w-4 h-4 text-emerald-500" />,
        badge: 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400',
        text: `${total} tasks (Available)`,
        level: 'low'
      };
    }
  };

  const availableSecondaryUsers = users.filter(u => 
    u.id !== formData.assigned_to && 
    !formData.secondary_participants.includes(u.id)
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            Create New Task
            <Sparkles className="w-5 h-5 text-violet-500" />
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="task-name">Task Name *</Label>
            <Input
              id="task-name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter task name"
              required
              data-testid="task-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="task-description">Description (Optional)</Label>
            <Textarea
              id="task-description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Add task details..."
              rows={3}
              data-testid="task-description-input"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Due Date *</Label>
              <Popover open={dateOpen} onOpenChange={setDateOpen} modal={true}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                    data-testid="due-date-btn"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.due_date ? format(formData.due_date, 'PPP') : 'Select date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 z-[200]" align="center" side="bottom" sideOffset={4}>
                  <Calendar
                    mode="single"
                    selected={formData.due_date}
                    onSelect={(date) => {
                      setFormData({ ...formData, due_date: date });
                      setDateOpen(false);
                    }}
                    disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>Primary Assignee *</Label>
              <Select
                value={formData.assigned_to}
                onValueChange={(value) => {
                  setFormData({ 
                    ...formData, 
                    assigned_to: value,
                    secondary_participants: formData.secondary_participants.filter(id => id !== value)
                  });
                }}
              >
                <SelectTrigger data-testid="assign-to-select">
                  <SelectValue placeholder="Select primary assignee" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((u) => {
                    const workloadInfo = getWorkloadIndicator(u.id);
                    return (
                      <SelectItem 
                        key={u.id} 
                        value={u.id} 
                        data-testid={`user-option-${u.id}`}
                        className="py-2"
                      >
                        <div className="flex items-center gap-2 w-full">
                          <span className="flex-1">
                            {u.name} {u.id === user?.id && '(You)'}
                          </span>
                          {workloadInfo && (
                            <span className="flex items-center gap-1">
                              {workloadInfo.icon}
                              <span className={`text-xs px-1.5 py-0.5 rounded ${workloadInfo.badge}`}>
                                {workloadInfo.text}
                              </span>
                            </span>
                          )}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Selected assignee workload warning */}
          {formData.assigned_to && (() => {
            const workloadInfo = getWorkloadIndicator(formData.assigned_to);
            if (workloadInfo && (workloadInfo.level === 'critical' || workloadInfo.level === 'high')) {
              const selectedUser = users.find(u => u.id === formData.assigned_to);
              return (
                <div className={`p-3 rounded-lg border ${
                  workloadInfo.level === 'critical' 
                    ? 'bg-red-500/10 border-red-500/30' 
                    : 'bg-orange-500/10 border-orange-500/30'
                }`}>
                  <div className="flex items-center gap-2">
                    {workloadInfo.icon}
                    <span className={`font-medium ${
                      workloadInfo.level === 'critical' ? 'text-red-600 dark:text-red-400' : 'text-orange-600 dark:text-orange-400'
                    }`}>
                      Warning: {selectedUser?.name} is {workloadInfo.level === 'critical' ? 'severely overloaded' : 'heavily loaded'}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Consider assigning to someone else or marking this as lower priority.
                  </p>
                </div>
              );
            }
            return null;
          })()}

          {/* Secondary Participants */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <UserPlus className="w-4 h-4" />
              Secondary Participants (Optional)
            </Label>
            
            {/* Selected secondary participants */}
            {formData.secondary_participants.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {formData.secondary_participants.map(pId => {
                  const participant = users.find(u => u.id === pId);
                  const workloadInfo = getWorkloadIndicator(pId);
                  return (
                    <Badge 
                      key={pId} 
                      variant="secondary" 
                      className="flex items-center gap-1 pr-1"
                    >
                      <Avatar className="h-4 w-4">
                        <AvatarFallback className="text-[10px]">
                          {participant?.name?.charAt(0) || '?'}
                        </AvatarFallback>
                      </Avatar>
                      <span>{participant?.name || 'Unknown'}</span>
                      {workloadInfo && workloadInfo.icon}
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-4 w-4 ml-1 hover:bg-destructive/20"
                        onClick={() => removeSecondaryParticipant(pId)}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </Badge>
                  );
                })}
              </div>
            )}
            
            {/* Add secondary participant dropdown */}
            {availableSecondaryUsers.length > 0 && (
              <Select onValueChange={addSecondaryParticipant}>
                <SelectTrigger data-testid="add-secondary-select">
                  <SelectValue placeholder="Add secondary participant..." />
                </SelectTrigger>
                <SelectContent>
                  {availableSecondaryUsers.map((u) => {
                    const workloadInfo = getWorkloadIndicator(u.id);
                    return (
                      <SelectItem key={u.id} value={u.id}>
                        <div className="flex items-center gap-2 w-full">
                          <span className="flex-1">
                            {u.name} {u.id === user?.id && '(You)'}
                          </span>
                          {workloadInfo && (
                            <span className="flex items-center gap-1">
                              {workloadInfo.icon}
                              <span className="text-xs">{workloadInfo.text}</span>
                            </span>
                          )}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            )}
            <p className="text-xs text-muted-foreground">
              Secondary participants can view and collaborate but are not the primary owner
            </p>
          </div>

          <div className="p-3 rounded-lg bg-violet-500/10 border border-violet-500/20">
            <div className="flex items-center gap-2 text-sm text-violet-600 dark:text-violet-400">
              <Sparkles className="w-4 h-4" />
              <span>AI will automatically prioritize this task based on content and due date</span>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} data-testid="cancel-task-btn">
              Cancel
            </Button>
            <Button type="submit" disabled={loading} data-testid="submit-task-btn">
              {loading ? 'Creating...' : 'Create Task'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateTaskModal;
